<vw-header-padding-safe-area></vw-header-padding-safe-area>
<!-- Compte rangé comme les réglages d'un téléphone (PC-10) : qui je suis, la
     carte Premium, puis un groupe par sujet, une ligne par réglage, une feuille
     par édition. La déconnexion n'est plus cachée sous une roue dentée. -->
<div class="px-4 pb-28">
  <h1 class="font-display text-display text-vw-ink pb-4">{{ "profile.title" | translate }}</h1>

  <!-- Qui je suis : la photo, le nom et l'ancienneté, sans libellés de formulaire. -->
  @if (user && !isLoading) {
    <div class="flex items-center gap-4 pb-6">
      @if (userPictureUrl) {
        <vw-img [rounded]="true" class="block h-24 w-24 shrink-0 overflow-hidden rounded-pill" [src]="userPictureUrl"
          aria-hidden="true"></vw-img>
      } @else {
        <span class="flex h-24 w-24 shrink-0 items-center justify-center rounded-pill bg-vw-surface-tint font-display text-display text-vw-primary-ink"
          aria-hidden="true">
          @if (initial) {
            {{ initial }}
          } @else {
            <vw-circle-user-icon class="block h-12 w-12" />
          }
        </span>
      }
      <!-- Une seule ligne, coupée par une ellipse : une adresse e-mail ne se
           casse pas au milieu d'un mot (« …@gmail.co / m »). -->
      <div class="min-w-0 flex-1">
        @if (displayName) {
          <p class="vw-account-name truncate text-title-m text-vw-ink">{{ displayName }}</p>
        }
        <p class="text-body-s text-vw-muted">
          {{ "profile.infos.joined" | translate }} {{ user.registrationDate | vwDate: 'month' }}
        </p>
      </div>
    </div>
  }
  @if (state.status !== 'ready') {
    <vw-state class="block pb-6" [state]="state" (retry)="load()">
      <vw-skeleton-loader skeleton [width]="'100%'" [height]="'96px'" [borderRadius]="'0.75rem'"></vw-skeleton-loader>
    </vw-state>
  }

  <div class="flex flex-col gap-6">
    <!-- Carte Premium : le profil est l'endroit attendu pour gerer un
         abonnement. Abonne et non-abonne ne partagent pas la meme carte : quand
         on paie, on doit voir qu'on est membre. Premium fermé : ni offre ni
         abonnement à gérer, la carte disparaît. -->
    @if (premiumService.premiumEnabled()) {
    <div class="w-full">
      @if (!premiumService.ready()) {
        <!-- État inconnu : un abonné ne doit pas voir l'offre payante
             clignoter le temps que le jeton soit lu. -->
        <vw-skeleton-loader [width]="'100%'" [height]="'84px'" [borderRadius]="'0.75rem'"></vw-skeleton-loader>
      } @else if (premiumService.isPremium()) {
      <!-- Même ligne compacte que l'offre, en vert foncé : on voit qu'on est
           membre, depuis quand et ce qui se passe ensuite ; le chevron mène à
           l'abonnement. -->
      <button type="button"
        class="flex w-full items-center gap-3 rounded-card bg-gradient-to-br from-vw-premium-deep-from to-vw-premium-deep-to px-4 py-3 text-left"
        (click)="openPremium()"
      >
        <span
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-gradient-to-br from-vw-premium-accent to-vw-premium-line"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" class="h-5 w-5 text-white" fill="currentColor" aria-hidden="true">
            <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z" />
          </svg>
        </span>
        <span class="flex min-w-0 grow flex-col">
          <span class="text-body font-semibold text-white">{{ 'premium.profile.member-badge' | translate }}</span>
          @if (premiumSinceLabel) {
            <span class="text-body-s text-white/75">
              {{ 'premium.profile.member-since' | translate: { date: premiumSinceLabel } }}
            </span>
          }
          @if (premiumNextLabel) {
            <span class="text-caption text-white/75">{{ premiumNextLabel }}</span>
          }
          <span class="sr-only">{{ 'premium.profile.manage' | translate }}</span>
        </span>
        <vw-chevron-right-icon class="block h-6 w-6 shrink-0 text-white/75" aria-hidden="true" />
      </button>
      } @else if (activatingPremium) {
        <!-- Retour du paiement (P4.0.2) : la carte de membre en attente, à la
             place de l'offre « 30 jours offerts » qui contredisait le paiement. -->
        <div class="flex w-full items-center gap-3 rounded-card bg-gradient-to-br from-vw-premium-deep-from to-vw-premium-deep-to px-4 py-3"
          role="status" data-testid="premium-activating">
          <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-white/15 text-vw-on-color" aria-hidden="true">
            <vw-loading-icon class="block h-5 w-5" />
          </span>
          <span class="flex min-w-0 grow flex-col">
            <span class="text-body font-semibold text-vw-on-color">{{ 'premium.paywall.activating' | translate }}</span>
            <span class="text-body-s text-white/75">{{ 'premium.paywall.activating-detail' | translate }}</span>
          </span>
        </div>
      } @else {
        <vw-premium-upsell-card source="profile_card" (ctaClicked)="openPremium()" />
      }
    </div>
    }


    @if (user && !isLoading) {
    <vw-list-section [title]="'profile.groups.profile' | translate">
      <vw-list-row [title]="'profile.edit-account.title' | translate"
        [subtitle]="infosSummary"
        (rowClick)="editUserMode = true">
        <vw-circle-user-icon vwListRowIcon class="block h-6 w-6" />
      </vw-list-row>
      <!-- La langue a sa ligne et sa feuille : six langues, un toucher, sans
           passer par tout le formulaire « Modifier mes infos ». -->
      <vw-list-row [title]="'profile.infos.language' | translate"
        [subtitle]="languageName"
        (rowClick)="languageMode = true">
        <vw-earth-icon vwListRowIcon class="block h-6 w-6" />
      </vw-list-row>
    </vw-list-section>
    }

    <vw-list-section [title]="'profile.garden.title' | translate">
      @if (state.status === 'ready') {
        @for (address of userAddresses; track address.uid) {
          <vw-user-address-card class="block" [address]="address" (closeEditAddressToast)="onCloseEditAddressToast()">
          </vw-user-address-card>
        }
      } @else if (isLoading) {
        <vw-skeleton-loader [width]="'100%'" [height]="'56px'"></vw-skeleton-loader>
      }
      <vw-list-row [title]="'garden.actions.handover.title' | translate"
        [subtitle]="'garden.actions.handover.subtitle' | translate" [size]="64" (rowClick)="openHandover()">
        <vw-shovel-icon vwListRowIcon class="block h-6 w-6" />
      </vw-list-row>
    </vw-list-section>

    <vw-list-section [title]="'profile.groups.notifications' | translate">
      <vw-list-row [title]="'profile.notification-preferences.entry' | translate" (rowClick)="notificationPrefsMode = true">
        <vw-bell-icon vwListRowIcon class="block h-6 w-6" />
      </vw-list-row>
      <!-- Refus du système (coque 1.5.1+) : sans cette ligne, quelqu'un qui a
           dit non ne saurait jamais pourquoi aucune alerte n'arrive. -->
      @if (pushPermission.alertsOff()) {
        <vw-list-row tone="warn" [title]="'profile.alerts-off.title' | translate"
          [subtitle]="'profile.alerts-off.text' | translate" [size]="64" (rowClick)="pushPermission.openSettings()">
          <vw-bell-icon vwListRowIcon class="block h-6 w-6" />
        </vw-list-row>
      }
    </vw-list-section>

    <vw-list-section [title]="'profile.groups.help' | translate">
      <vw-list-row [title]="'home.feedback.title' | translate" (rowClick)="openFeedback()">
        <vw-message-circle-question-icon vwListRowIcon class="block h-6 w-6" />
      </vw-list-row>
    </vw-list-section>

    <vw-list-section [title]="'profile.groups.account' | translate">
      <vw-list-row [title]="'profile.logout' | translate" [chevron]="false" (rowClick)="logout()">
        <vw-logout-icon vwListRowIcon class="block h-6 w-6" />
      </vw-list-row>
      <vw-list-row tone="danger" [title]="'profile.delete-account.title' | translate" [chevron]="false"
        (rowClick)="onDeleteAccount()">
        <vw-delete-icon vwListRowIcon class="block h-6 w-6" />
      </vw-list-row>
    </vw-list-section>
  </div>
</div>

<vw-edit-user-toast [isOpen]="editUserMode" [user]="user" (close)="onCloseEditUser($event)"></vw-edit-user-toast>
<vw-language-toast [isOpen]="languageMode" [current]="user?.preferredLanguage ?? null"
  (close)="onLanguageClosed($event)"></vw-language-toast>
<vw-notification-preferences-toast [isOpen]="notificationPrefsMode"
  (close)="notificationPrefsMode = false"></vw-notification-preferences-toast>
<!-- Suppression du compte : la confirmation destructive commune (bouton rouge
     « Supprimer mon compte », « Annuler » discret dessous). -->
<vw-confirm-dialog
  [isOpen]="showConfirmDelete"
  [title]="'profile.delete-account.question' | translate"
  [message]="'profile.delete-account.description' | translate"
  [confirmLabel]="'profile.delete-account.confirm' | translate"
  [cancelLabel]="'profile.delete-account.cancel'"
  [loading]="deletingAccount"
  [error]="deleteAccountFailed ? ('profile.delete-account.error' | translate) : ''"
  (confirmed)="onConfirmDelete()"
  (cancelled)="closeDeleteConfirm()"
></vw-confirm-dialog>


<vw-premium-toast
  [isOpen]="premiumPaywallMode"
  [source]="'profile_card'"
  (close)="premiumPaywallMode = false"
></vw-premium-toast>

<vw-subscription-toast
  [isOpen]="subscriptionMode && premiumService.premiumEnabled()"
  (close)="onSubscriptionToastClosed($event)"
></vw-subscription-toast>

<vw-downgrade-toast
  [isOpen]="premiumService.justDowngraded()"
  (close)="onDowngradeAcknowledged($event)"
></vw-downgrade-toast>
